import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Clock, Send } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { BRAND } from '../data/brand';
import { useAccess } from '../context/AccessContext';

const ContactPage = () => {
  const navigate = useNavigate();
  const { memberEmail } = useAccess();
  const [formData, setFormData] = useState({
    name: '',
    email: memberEmail || '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate submission
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setIsSubmitting(false);
    setIsSubmitted(true);
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <Header />
        <div className="min-h-[80vh] flex items-center justify-center px-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="max-w-md text-center"
          >
            <div className="w-16 h-16 border border-white/20 rounded-full mx-auto mb-8 flex items-center justify-center">
              <Send className="w-6 h-6 text-white" />
            </div>
            <h2 className="font-serif text-3xl text-zinc-50 mb-4">
              Message Received
            </h2>
            <p className="font-sans text-sm text-zinc-500 mb-8">
              Our concierge team will respond within 24 hours.
            </p>
            <button
              onClick={() => navigate('/collection')}
              className="border border-zinc-700 text-zinc-400 px-8 py-4 font-sans text-xs font-medium tracking-[0.2em] uppercase hover:border-white hover:text-white transition-all duration-300"
            >
              Return to Collection
            </button>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-12 px-6 md:px-12 lg:px-24">
        <div className="max-w-3xl mx-auto text-center">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="font-sans text-xs tracking-[0.3em] uppercase text-zinc-500 mb-4"
          >
            Get in Touch
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-zinc-50 mb-6"
          >
            {BRAND.concierge.headline}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="font-sans text-base text-zinc-500 max-w-lg mx-auto"
          >
            {BRAND.concierge.intro}
          </motion.p>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-8 px-6 md:px-12 lg:px-24">
        <div className="max-w-xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="flex flex-wrap justify-center gap-8 mb-12"
          >
            <div className="flex items-center gap-3 text-zinc-500">
              <Clock className="w-4 h-4" />
              <span className="font-sans text-xs">{BRAND.concierge.availability}</span>
            </div>
            <div className="flex items-center gap-3 text-zinc-500">
              <Mail className="w-4 h-4" />
              <span className="font-sans text-xs">{BRAND.concierge.responseTime}</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-8 px-6 md:px-12 lg:px-24">
        <div className="max-w-xl mx-auto">
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            onSubmit={handleSubmit}
            className="space-y-6"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label className="block font-sans text-xs text-zinc-600 tracking-wide uppercase mb-2">
                  Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  data-testid="contact-name"
                  className="w-full bg-transparent border-b border-zinc-800 focus:border-white py-3 text-white font-sans text-sm placeholder:text-zinc-700 transition-colors outline-none"
                  placeholder="Your name"
                />
              </div>
              <div>
                <label className="block font-sans text-xs text-zinc-600 tracking-wide uppercase mb-2">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  data-testid="contact-email"
                  className="w-full bg-transparent border-b border-zinc-800 focus:border-white py-3 text-white font-sans text-sm placeholder:text-zinc-700 transition-colors outline-none"
                  placeholder="Your email"
                />
              </div>
            </div>

            <div>
              <label className="block font-sans text-xs text-zinc-600 tracking-wide uppercase mb-2">
                Subject
              </label>
              <select
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                required
                data-testid="contact-subject"
                className="w-full bg-transparent border-b border-zinc-800 focus:border-white py-3 text-white font-sans text-sm transition-colors outline-none appearance-none cursor-pointer"
              >
                <option value="" className="bg-[#0A0A0A]">Select a topic</option>
                <option value="order" className="bg-[#0A0A0A]">Order Inquiry</option>
                <option value="product" className="bg-[#0A0A0A]">Product Question</option>
                <option value="membership" className="bg-[#0A0A0A]">Membership</option>
                <option value="feedback" className="bg-[#0A0A0A]">Feedback</option>
                <option value="other" className="bg-[#0A0A0A]">Other</option>
              </select>
            </div>

            <div>
              <label className="block font-sans text-xs text-zinc-600 tracking-wide uppercase mb-2">
                Message
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows={5}
                data-testid="contact-message"
                className="w-full bg-transparent border border-zinc-800 focus:border-white p-4 text-white font-sans text-sm placeholder:text-zinc-700 transition-colors outline-none resize-none"
                placeholder="How can we help?"
              />
            </div>

            <p className="font-sans text-xs text-zinc-700">
              {BRAND.concierge.note}
            </p>

            <button
              type="submit"
              disabled={isSubmitting}
              data-testid="contact-submit"
              className="w-full bg-white text-black py-4 font-sans text-xs font-medium tracking-[0.2em] uppercase hover:bg-zinc-200 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isSubmitting ? (
                <div className="w-5 h-5 border-2 border-black/20 border-t-black rounded-full animate-spin" />
              ) : (
                'Send Message'
              )}
            </button>
          </motion.form>
        </div>
      </section>

      {/* FAQ Link */}
      <section className="py-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="font-sans text-sm text-zinc-500 mb-4">
              Looking for quick answers?
            </p>
            <button
              onClick={() => navigate('/faq')}
              className="text-zinc-400 hover:text-white font-sans text-xs tracking-[0.2em] uppercase transition-colors"
            >
              View FAQ →
            </button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
          <p className="font-serif text-xl text-zinc-50">Secret 13</p>
          <p className="font-sans text-xs text-zinc-600">
            {BRAND.legal.copyright} {BRAND.legal.membersOnly}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default ContactPage;
